import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { post } from 'axios';

import { actionSvgs, callbackSvgs, contentSvgs } from 'assets/icons';

import If from 'components/If';
import Tooltip from 'components/Informations/Tooltip';

import { secondsToHours, formatDate, dayOfWeek, sumHours } from 'utils/moment';
import endpoints from 'services/endpoints';

import { appointmentStatus } from './utils';
import * as S from './styles';

const { updateAppointmentsFromAhgora } = endpoints;

export default function AppointmentsTable({
  data,
  user,
  selectAppointment,
  currentTableRow,
  setCurrentTableRow,
  resetSelections,
  setToastVisibility,
  setToastInfos,
  reloadAppointments,
  setLoading,
  selectedEmployee,
  isAdminVisibility,
}) {
  const [appointments, setAppointments] = useState([]);
  const [orderBy, setOrderBy] = useState('date');

  const { bate_ponto: batePonto, id_funcionario: idFuncionario } = user;
  const { resumoDoMes } = data;

  useEffect(() => {
    setAppointments(resumoDoMes);
  }, [data, resumoDoMes]);

  function handleTableOrder(order) {
    if (order === 'date')
      return [setAppointments(resumoDoMes), setOrderBy('date')];

    const ordered = _.orderBy(appointments, ['horasEmProjeto']);
    return [setAppointments(ordered), setOrderBy('status')];
  }

  const handleAppointmentRefresh = useCallback(
    async date => {
      try {
        resetSelections();
        setLoading(true);

        await post(updateAppointmentsFromAhgora, {
          id_funcionario: selectedEmployee,
          date,
          id_usr: idFuncionario,
        });

        reloadAppointments();
        setToastVisibility(true);
        setToastInfos({
          message: 'Apontamentos atualizados com sucesso',
          type: 'success',
          closeTime: 6000,
        });
        setLoading(false);
      } catch {
        resetSelections();
        setToastVisibility(true);
        setToastInfos({
          message: 'Erro ao atualizar apontamentos',
          type: 'error',
          closeTime: 6000,
        });
        setLoading(false);
      }
    },
    [
      idFuncionario,
      setToastInfos,
      setToastVisibility,
      selectedEmployee,
      reloadAppointments,
      setLoading,
      resetSelections,
    ],
  );

  return (
    <S.Table>
      <div className="table-responsive">
        <If condition={!appointments.length}>
          <S.CallbackContainer>
            {callbackSvgs('circularMan')}
            <S.CallbackText>
              Não foram encontrados resultados. Verifique se o mês selecionado
              antecede a sua entrada na empresa
            </S.CallbackText>
          </S.CallbackContainer>
        </If>

        <If condition={appointments.length}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col" onClick={() => handleTableOrder('date')}>
                  Data
                  <If condition={orderBy === 'date'}>
                    {contentSvgs('tableArrow')}
                  </If>
                </th>
                <th scope="col">Horas trabalhadas</th>
                <th scope="col">Horas apontadas</th>
                <th scope="col" onClick={() => handleTableOrder('status')}>
                  Status
                  <If condition={orderBy === 'status'}>
                    {contentSvgs('tableArrow')}
                  </If>
                </th>

                <If condition={isAdminVisibility}>
                  <th> </th>
                </If>
              </tr>
            </thead>
            <tbody>
              {appointments.map(appointment => (
                <tr
                  key={appointment.date}
                  onClick={() => {
                    if (appointment.date === currentTableRow) {
                      return resetSelections();
                    }

                    return [
                      selectAppointment(appointment),
                      setCurrentTableRow(appointment.date),
                    ];
                  }}
                  className={
                    appointment.date === currentTableRow ? 'active' : ''
                  }
                >
                  <td>
                    {formatDate(appointment.date)}
                    <span>{` (${dayOfWeek(appointment.date)})`}</span>
                  </td>

                  <td>
                    {secondsToHours(appointment.totalHorasPonto)}

                    <If
                      condition={
                        appointment.horasPonto.length % 2 !== 0 && batePonto
                      }
                    >
                      <Tooltip>
                        <p>
                          O total de horas trabalhadas está incorreto pois
                          existem batidas ímpares no Ahgora que precisam ser
                          regularizadas.
                        </p>
                      </Tooltip>
                    </If>
                  </td>

                  <td>
                    {sumHours(appointment.horasEmProjeto, 'horas_apontadas')}
                  </td>

                  <td
                    className={`status-${appointmentStatus(
                      appointment,
                      'status',
                    )}`}
                  >
                    {appointmentStatus(appointment, 'text')}
                  </td>

                  <If condition={isAdminVisibility}>
                    <S.RefreshButton
                      onClick={() => {
                        handleAppointmentRefresh(appointment.date);
                      }}
                    >
                      <td title="Atualizar apontamentos">
                        {actionSvgs('refresh')}
                      </td>
                    </S.RefreshButton>
                  </If>
                </tr>
              ))}
            </tbody>
          </table>
        </If>
      </div>
    </S.Table>
  );
}

AppointmentsTable.defaultProps = {
  user: {},
  currentTableRow: {},
  selectedEmployee: null,
};

AppointmentsTable.propTypes = {
  data: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
  ).isRequired,
  user: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ),
  setToastVisibility: PropTypes.func.isRequired,
  setToastInfos: PropTypes.func.isRequired,
  selectAppointment: PropTypes.func.isRequired,
  currentTableRow: PropTypes.number,
  setCurrentTableRow: PropTypes.func.isRequired,
  reloadAppointments: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  resetSelections: PropTypes.func.isRequired,
  selectedEmployee: PropTypes.number,
  isAdminVisibility: PropTypes.bool.isRequired,
};
